// ElevenLabs TTS + Web Speech API fallback
const TTS_URL = '/tts-proxy';
const audioCache = new Map<string, string>(); // text → blob URL
let currentAudio: HTMLAudioElement | null = null;

export async function speak(text: string, lang: 'ar' | 'en' = 'ar', _rate = 0.8) {
  if (!text.trim()) return;

  // Stop any playing audio
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }

  // Check cache
  if (audioCache.has(text)) {
    const url = audioCache.get(text)!;
    currentAudio = new Audio(url);
    currentAudio.play().catch(() => fallbackSpeak(text, lang));
    return;
  }

  // Try ElevenLabs via proxy
  try {
    const resp = await fetch(TTS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, lang }),
    });
    if (resp.ok) {
      const blob = await resp.blob();
      if (blob.size > 500) {
        const url = URL.createObjectURL(blob);
        audioCache.set(text, url);
        currentAudio = new Audio(url);
        currentAudio.play().catch(() => fallbackSpeak(text, lang));
        return;
      }
    }
  } catch (_) {}

  // Fallback to browser TTS
  fallbackSpeak(text, lang);
}

function fallbackSpeak(text: string, lang: 'ar' | 'en') {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = lang === 'ar' ? 'ar-SA' : 'en-US';
  utter.rate = 0.85;
  utter.pitch = 1.1;
  utter.volume = 1;
  const voices = window.speechSynthesis.getVoices();
  const voice = voices.find(v => v.lang.startsWith(lang === 'ar' ? 'ar' : 'en'));
  if (voice) utter.voice = voice;
  window.speechSynthesis.speak(utter);
}

export function playSuccessSound() {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  [523, 659, 784, 1047].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = freq; osc.type = 'sine';
    gain.gain.setValueAtTime(0.3, ctx.currentTime + i * 0.15);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3);
    osc.start(ctx.currentTime + i * 0.15);
    osc.stop(ctx.currentTime + i * 0.15 + 0.3);
  });
}

export function playErrorSound() {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.frequency.value = 220; osc.type = 'sawtooth';
  gain.gain.setValueAtTime(0.2, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
  osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.5);
}

export function playClickSound() {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain); gain.connect(ctx.destination);
  osc.frequency.value = 800; osc.type = 'sine';
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
  osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.1);
}

export function playStarSound() {
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  [880, 1100, 1320].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain); gain.connect(ctx.destination);
    osc.frequency.value = freq; osc.type = 'sine';
    gain.gain.setValueAtTime(0.2, ctx.currentTime + i * 0.1);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.1 + 0.2);
    osc.start(ctx.currentTime + i * 0.1);
    osc.stop(ctx.currentTime + i * 0.1 + 0.2);
  });
}
